import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CreditRequest {
  id: string;
  product_id: string;
  product_name: string;
  order_id: string;
  credit_reason_id: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  notes: string | null;
  photos: string[];
  status: string;
  requested_by: string | null;
  created_from_source: string;
  created_at: string;
  updated_at: string;
}

export interface CreateCreditRequestData {
  product_id: string;
  product_name: string;
  order_id: string;
  credit_reason_id: string;
  quantity: number;
  unit_price: number;
  total_amount: number;
  notes?: string;
  photos?: string[];
  requested_by?: string;
  reason: string;
  invoice_item_id?: string | null;
}

export const useCreditRequestsByOrder = (orderId: string) => {
  return useQuery({
    queryKey: ['credit-requests', orderId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_reports')
        .select(`
          *,
          credit_reasons (
            reason_name,
            description
          )
        `)
        .eq('order_id', orderId)
        .eq('created_from_source', 'pos');
      
      if (error) throw error;
      return data;
    },
    enabled: !!orderId,
  });
};

export const useCreditRequestsByProduct = (productId: string) => {
  return useQuery({
    queryKey: ['credit-requests', 'product', productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('credit_reports')
        .select(`
          *,
          credit_reasons (
            reason_name,
            description
          )
        `)
        .eq('product_id', productId)
        .eq('created_from_source', 'pos');
      
      if (error) throw error;
      return data;
    },
    enabled: !!productId,
  });
};

export const useCreateCreditRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (data: CreateCreditRequestData) => {
      const { data: result, error } = await supabase
        .from('credit_reports')
        .insert({
          product_id: data.product_id,
          product_name: data.product_name,
          order_id: data.order_id,
          credit_reason_id: data.credit_reason_id,
          quantity: data.quantity,
          unit_price: data.unit_price,
          total_amount: data.total_amount,
          notes: data.notes || null,
          photos: data.photos || [],
          requested_by: data.requested_by || null,
          status: 'pending',
          created_from_source: 'pos',
          reason: data.reason,
          invoice_item_id: data.invoice_item_id || null,
        })
        .select()
        .single();
      
      if (error) {
        console.error('Credit request creation error:', error);
        throw error;
      }
      return result;
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['credit-requests'] });
      toast({
        title: "Success",
        description: "Credit request created successfully",
      });
    },
    onError: (error: any) => {
      console.error('Credit request error:', error);
      toast({
        title: "Error",
        description: error?.message || "Failed to create credit request",
        variant: "destructive",
      });
    },
  });
};

export const useUpdateCreditRequest = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<CreditRequest> & { id: string }) => {
      // Recalculate total if quantity or unit_price changed
      if (updates.quantity || updates.unit_price) {
        const { data: current } = await supabase
          .from('credit_reports')
          .select('quantity, unit_price')
          .eq('id', id)
          .single();
        
        if (current) {
          const quantity = updates.quantity ?? current.quantity;
          const unit_price = updates.unit_price ?? current.unit_price;
          updates.total_amount = quantity * unit_price;
        }
      }

      const { data, error } = await supabase
        .from('credit_reports')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['credit-requests'] });
      toast({
        title: "Success",
        description: "Credit request updated successfully",
      });
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: "Failed to update credit request",
        variant: "destructive",
      });
    },
  });
};